/**
 * Card helper predicates ported from legacy `public/js/utils/cardHelpers.js`
 * (only the functions the CardDetails tree consumes).
 */
import get from 'lodash/get'
import { CARD_CAPABILITIES, SHIPPING_STATUS, MAX_SECONDARY_CARDS_ALLOWED } from './constants'
import type { Card } from '../types'

export function checkIfCardNeedsActivation(card: Partial<Card> = {}): boolean {
  const cardCapabilities = card?.capabilities || []
  return cardCapabilities.includes(CARD_CAPABILITIES.ACTIVATE)
}

export function checkIfCardIsNotShipped(card: Partial<Card> = {}): boolean {
  const cardShipmentStatus =
    card?.shipment_details?.shipment_status || card?.shipmentDetails?.shipmentStatus
  return cardShipmentStatus === SHIPPING_STATUS.NOT_SHIPPED
}

export function isShowCardAllowed(card: Partial<Card> = { capabilities: [] }): boolean {
  return (card?.capabilities || []).includes(CARD_CAPABILITIES.SHOW_CARD)
}

function getAdditionalDetailCapabilities(card: Partial<Card> = {}): string[] {
  return card?.additional_details?.capabilities || card?.additionalDetails?.capabilities || []
}

export function isInstantCard(card: Partial<Card> = {}): boolean {
  return getAdditionalDetailCapabilities(card).includes('INSTANT_ACCESS')
}

/**
 * Legacy debitcardnodeweb: ACTIVE, or REISSUED + instant on the unlock response card.
 * Ucs wraps `{ success, card }` — require lifecycle on `result.card` (not `success` alone,
 * and no UI-card fallback for instant).
 */
export function isUnlockSuccessful(result: any): boolean {
  const unlockedCard = result?.card
  if (!unlockedCard) {
    return false
  }
  const lifecycle = unlockedCard.lifecycle_state
  if (lifecycle === 'ACTIVE') {
    return true
  }
  return lifecycle === 'REISSUED' && isInstantCard(unlockedCard)
}

export function findPrimaryCard(cards: Card[] | Card | null | undefined): Card | null {
  if (cards) {
    if (Array.isArray(cards)) {
      return cards.find((card) => card.primary_card) || null
    } else if ((cards as Card).primary_card) {
      return cards as Card
    }
  }
  return null
}

export function getPrimaryCount(cards: Card[] | Card | null | undefined): number {
  const primaryCard = findPrimaryCard(cards)
  if (primaryCard && primaryCard.card_id) {
    return 1
  }
  return 0
}

export function getSecondaryCount(cards: Card[] = []): number {
  let secondaryCount = 0
  for (let i = 0; i < cards.length; i++) {
    if (!cards[i].primary_card) {
      secondaryCount++
    }
  }
  return secondaryCount
}

export function getMaxSecondaryCardsAllowed(primaryCard: Card | null): number {
  return get(
    primaryCard,
    'additional_details.additional_cards.maximum_allowed',
    MAX_SECONDARY_CARDS_ALLOWED,
  )
}

export function getMaxSecondaryCardsAllowedFromCards(cards: Card[]): number {
  const primaryCard = findPrimaryCard(cards)
  return getMaxSecondaryCardsAllowed(primaryCard)
}

export function getSecondaryCardsRemaining(cards: Card[] = []): number {
  const maxSecondaryCardsAllowed = getMaxSecondaryCardsAllowedFromCards(cards)
  const currentSecondaryCardsCount = getSecondaryCount(cards)
  return maxSecondaryCardsAllowed - currentSecondaryCardsCount
}

export function getTotalNumberOfCardsRemaining(cards: Card[] = []): number {
  const noOfExistingSecondaryCards = getSecondaryCount(cards)
  const primaryCard = findPrimaryCard(cards)
  if (!primaryCard) {
    const maxAdditionalCards = MAX_SECONDARY_CARDS_ALLOWED
    return maxAdditionalCards - noOfExistingSecondaryCards + 1
  }

  const maxAdditionalCards = getMaxSecondaryCardsAllowed(primaryCard)
  let numberOfAdditionalCardsAllowed = maxAdditionalCards - noOfExistingSecondaryCards
  const accountHasMaxCardsAllowed = primaryCard && cards.length >= maxAdditionalCards + 1
  if (accountHasMaxCardsAllowed || numberOfAdditionalCardsAllowed < 0) {
    numberOfAdditionalCardsAllowed = 0
  }

  return numberOfAdditionalCardsAllowed
}
